import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { emailLogin, emailSignup, forgotPassword, getRoles } from "@/services/authService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Mail, Lock, User, Phone, Calendar, Briefcase, 
  Eye, EyeOff, ArrowRight, ArrowLeft, Check, Loader2,
  Sparkles
} from "lucide-react";
import { Logo1895 } from "@/components/Logo1895";
import { useAuth } from "@/contexts/AuthContext";
import { setAuthToken } from "@/utils/auth";

// Cinema quotes for the login panel
const CINEMA_QUOTES = [
  { quote: "Cinema is a matter of what's in the frame and what's out.", author: "Martin Scorsese" },
  { quote: "A film is never really good unless the camera is an eye in the head of a poet.", author: "Orson Welles" },
  { quote: "The length of a film should be directly related to the endurance of the human bladder.", author: "Alfred Hitchcock" },
  { quote: "Cinema is the most beautiful fraud in the world.", author: "Jean-Luc Godard" },
  { quote: "Film is a battleground. Love, hate, violence, action, death. In a word: emotion.", author: "Samuel Fuller" },
];

// Film stills for login background
const FILM_STILLS = [
  "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=1200",
  "https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=1200",
  "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1200",
  "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=1200",
];

// Behind-the-scenes filmmaking images for signup steps
const SIGNUP_BACKGROUNDS = [
  {
    image: "https://images.unsplash.com/photo-1524712245354-2c4e5e7121c0?w=1600",
    caption: "Every great story starts with a name",
    credit: "On set"
  },
  {
    image: "https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?w=1600",
    caption: "Stay connected to your creative vision",
    credit: "Film production"
  },
  {
    image: "https://images.unsplash.com/photo-1505775561242-727b7fba20f0?w=1600",
    caption: "Your journey in cinema begins",
    credit: "Director's chair"
  },
  {
    image: "https://images.unsplash.com/photo-1493804714600-6edb1cd93080?w=1600",
    caption: "Secure your creative workspace",
    credit: "Behind the lens"
  }
];

// Step configuration
const SIGNUP_STEPS = [
  { id: 1, title: "Your Name", fields: ["firstName", "lastName"] },
  { id: 2, title: "Contact Info", fields: ["email", "mobile"] },
  { id: 3, title: "About You", fields: ["dateOfBirth", "role"] },
  { id: 4, title: "Secure Account", fields: ["password", "confirmPassword", "acceptTerms"] }
];

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login"); // login | signup | forgot
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState([]);
  
  // Quote rotation for login
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [bgIndex, setBgIndex] = useState(0);
  
  // Signup step management
  const [signupStep, setSignupStep] = useState(1);
  
  // Login form
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
    rememberMe: false
  });
  
  // Signup form
  const [signupForm, setSignupForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    dateOfBirth: "",
    role: "",
    acceptTerms: false
  });
  
  // Forgot password
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    // Rotate quotes
    const quoteTimer = setInterval(() => {
      setQuoteIndex(i => (i + 1) % CINEMA_QUOTES.length);
    }, 8000);
    
    // Rotate backgrounds for login
    const bgTimer = setInterval(() => {
      setBgIndex(i => (i + 1) % FILM_STILLS.length);
    }, 12000);
    
    // Fetch roles
    fetchRoles();
    
    return () => {
      clearInterval(quoteTimer);
      clearInterval(bgTimer);
    };
  }, []);
  
  const fetchRoles = async () => {
    try {
      const data = await getRoles();
      setRoles(data.roles);
    } catch (e) {
      console.error("Failed to fetch roles");
    }
  };

  const handleGoogleAuth = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.location.href = `${backendUrl}/api/auth/google/login`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    try {
      const data = await emailLogin({
        email: loginForm.email,
        password: loginForm.password,
        remember_me: loginForm.rememberMe
      });
      
      if (data.token) setAuthToken(data.token);
      setUser(data.user);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail || "Login failed";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // Validate current signup step
  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!signupForm.firstName.trim()) {
          toast.error("Please enter your first name");
          return false;
        }
        if (!signupForm.lastName.trim()) {
          toast.error("Please enter your last name");
          return false;
        }
        return true;
      case 2:
        if (!signupForm.email.trim()) {
          toast.error("Please enter your email");
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupForm.email)) {
          toast.error("Please enter a valid email");
          return false;
        }
        if (!signupForm.mobile.trim()) {
          toast.error("Please enter your mobile number");
          return false;
        }
        return true;
      case 3:
        if (!signupForm.dateOfBirth) {
          toast.error("Please enter your date of birth");
          return false;
        }
        if (!signupForm.role) {
          toast.error("Please select your role");
          return false;
        }
        return true;
      case 4:
        if (signupForm.password.length < 8) {
          toast.error("Password must be at least 8 characters");
          return false;
        }
        if (signupForm.password !== signupForm.confirmPassword) {
          toast.error("Passwords do not match");
          return false;
        }
        if (!signupForm.acceptTerms) {
          toast.error("Please accept the terms and conditions");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNextStep = () => {
    if (validateStep(signupStep)) {
      if (signupStep < 4) {
        setSignupStep(signupStep + 1);
      } else {
        handleSignup();
      }
    }
  };

  const handlePrevStep = () => {
    if (signupStep > 1) {
      setSignupStep(signupStep - 1);
    }
  };

  const handleSignup = async () => {
    setLoading(true);
    try {
      const data = await emailSignup({
        first_name: signupForm.firstName,
        last_name: signupForm.lastName,
        email: signupForm.email,
        mobile: signupForm.mobile,
        password: signupForm.password,
        date_of_birth: signupForm.dateOfBirth,
        role: signupForm.role,
        accept_terms: signupForm.acceptTerms
      });
      
      if (data.token) setAuthToken(data.token);
      setUser(data.user);
      toast.success("Welcome to 1895! Check your email to verify your account.");
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail || "Signup failed";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email");
      return;
    }
    
    setLoading(true);
    try {
      await forgotPassword(forgotEmail);
      setForgotSent(true);
      toast.success("If an account exists, you'll receive a reset link");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const startSignup = () => {
    setMode("signup");
    setSignupStep(1);
    setSignupForm({
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
      password: "",
      confirmPassword: "",
      dateOfBirth: "",
      role: "",
      acceptTerms: false
    });
  };

  const currentQuote = CINEMA_QUOTES[quoteIndex];
  const currentSignupBg = SIGNUP_BACKGROUNDS[signupStep - 1];

  // Render signup step content
  const renderSignupStep = () => {
    switch (signupStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-gray-300 text-sm font-medium">First Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="text"
                  value={signupForm.firstName}
                  onChange={e => setSignupForm({...signupForm, firstName: e.target.value})}
                  className="pl-12 h-14 bg-white/10 border-white/20 text-white text-lg placeholder:text-gray-500 focus:border-amber-400 focus:ring-amber-400/20"
                  placeholder="John"
                  autoFocus
                  data-testid="signup-firstname"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300 text-sm font-medium">Last Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="text"
                  value={signupForm.lastName}
                  onChange={e => setSignupForm({...signupForm, lastName: e.target.value})}
                  className="pl-12 h-14 bg-white/10 border-white/20 text-white text-lg placeholder:text-gray-500 focus:border-amber-400 focus:ring-amber-400/20"
                  placeholder="Doe"
                  data-testid="signup-lastname"
                />
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-gray-300 text-sm font-medium">Email Address</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="email"
                  value={signupForm.email}
                  onChange={e => setSignupForm({...signupForm, email: e.target.value})}
                  className="pl-12 h-14 bg-white/10 border-white/20 text-white text-lg placeholder:text-gray-500 focus:border-amber-400 focus:ring-amber-400/20"
                  placeholder="you@example.com"
                  autoFocus
                  data-testid="signup-email"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300 text-sm font-medium">Mobile Number</Label>
              <div className="relative mt-2">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="tel"
                  value={signupForm.mobile}
                  onChange={e => setSignupForm({...signupForm, mobile: e.target.value})}
                  className="pl-12 h-14 bg-white/10 border-white/20 text-white text-lg placeholder:text-gray-500 focus:border-amber-400 focus:ring-amber-400/20"
                  placeholder="+1 (555) 123-4567"
                  data-testid="signup-mobile"
                />
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-gray-300 text-sm font-medium">Date of Birth</Label>
              <div className="relative mt-2">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="date"
                  value={signupForm.dateOfBirth}
                  onChange={e => setSignupForm({...signupForm, dateOfBirth: e.target.value})}
                  className="pl-12 h-14 bg-white/10 border-white/20 text-white text-lg [color-scheme:dark] focus:border-amber-400 focus:ring-amber-400/20"
                  data-testid="signup-dob"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300 text-sm font-medium">Your Role in Film</Label>
              <Select 
                value={signupForm.role} 
                onValueChange={v => setSignupForm({...signupForm, role: v})}
              >
                <SelectTrigger 
                  className="mt-2 h-14 bg-white/10 border-white/20 text-white text-lg focus:border-amber-400 focus:ring-amber-400/20"
                  data-testid="signup-role"
                >
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-gray-500" />
                    <SelectValue placeholder="Select your role" />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-[#1a1918] border-[#2a2826] max-h-[300px]">
                  {roles.map(r => (
                    <SelectItem 
                      key={r.value} 
                      value={r.value}
                      className="text-white hover:bg-amber-500/20 focus:bg-amber-500/20 cursor-pointer py-3"
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-gray-300 text-sm font-medium">Create Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={signupForm.password}
                  onChange={e => setSignupForm({...signupForm, password: e.target.value})}
                  className="pl-12 pr-12 h-14 bg-white/10 border-white/20 text-white text-lg placeholder:text-gray-500 focus:border-amber-400 focus:ring-amber-400/20"
                  placeholder="Min 8 characters"
                  autoFocus
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-gray-300 text-sm font-medium">Confirm Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="password"
                  value={signupForm.confirmPassword}
                  onChange={e => setSignupForm({...signupForm, confirmPassword: e.target.value})}
                  className="pl-12 h-14 bg-white/10 border-white/20 text-white text-lg placeholder:text-gray-500 focus:border-amber-400 focus:ring-amber-400/20"
                  placeholder="Confirm your password"
                  data-testid="signup-confirm-password"
                />
              </div>
            </div>
            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="terms"
                checked={signupForm.acceptTerms}
                onCheckedChange={checked => setSignupForm({...signupForm, acceptTerms: checked})}
                className="border-white/30 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500 mt-0.5 w-5 h-5"
                data-testid="signup-terms"
              />
              <label htmlFor="terms" className="text-sm text-gray-300 cursor-pointer leading-relaxed">
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
                  Terms and Conditions
                </Link>
              </label>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0908]">
      {/* Left Panel - Dynamic based on mode */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <AnimatePresence mode="wait">
          {mode === "signup" ? (
            // Signup background - changes per step
            <motion.div
              key={`signup-bg-${signupStep}`}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0"
            >
              <img 
                src={currentSignupBg.image} 
                alt="" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/80" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
            </motion.div>
          ) : (
            // Login background - rotating film stills
            <motion.div
              key={`login-bg-${bgIndex}`}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 1.5 }}
              className="absolute inset-0"
            >
              <img 
                src={FILM_STILLS[bgIndex]} 
                alt="" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/80" />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Overlay Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 h-full">
          {/* Top - Logo */}
          <div>
            <Logo1895 variant="full" width={140} className="opacity-90" />
          </div>
          
          {/* Bottom - Caption/Quote */}
          <AnimatePresence mode="wait">
            {mode === "signup" ? (
              <motion.div
                key={`caption-${signupStep}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-3xl font-light text-white/90 leading-relaxed max-w-md">
                  {currentSignupBg.caption}
                </p>
                <p className="text-amber-400/70 text-sm mt-4">
                  {currentSignupBg.credit}
                </p>
              </motion.div>
            ) : (
              <motion.div
                key={`quote-${quoteIndex}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.8 }}
              >
                <blockquote className="text-2xl font-light italic text-white/90 leading-relaxed max-w-lg">
                  "{currentQuote.quote}"
                </blockquote>
                <p className="text-amber-400 mt-4 font-medium">
                  — {currentQuote.author}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Right Panel - Auth Forms */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {/* LOGIN FORM */}
            {mode === "login" && (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Mobile Logo */}
                <div className="text-center mb-8 lg:hidden">
                  <Logo1895 variant="full" className="mx-auto" width={160} />
                </div>
                
                <div className="text-center lg:text-left">
                  <h1 className="text-2xl font-bold text-white">Welcome back</h1>
                  <p className="text-gray-400 mt-1">Sign in to continue to 1895</p>
                </div>

                {/* Google Auth Button */}
                <Button
                  onClick={handleGoogleAuth}
                  variant="outline"
                  className="w-full h-12 bg-white hover:bg-gray-100 text-black border-0"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#2a2826]" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#0a0908] px-4 text-gray-500">or</span>
                  </div>
                </div>

                {/* Email Login Form */}
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div>
                    <Label className="text-gray-400 text-xs">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        type="email"
                        value={loginForm.email}
                        onChange={e => setLoginForm({...loginForm, email: e.target.value})}
                        className="pl-10 bg-[#121110] border-[#2a2826] text-white"
                        placeholder="you@example.com"
                        data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-400 text-xs">Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={loginForm.password}
                        onChange={e => setLoginForm({...loginForm, password: e.target.value})}
                        className="pl-10 pr-10 bg-[#121110] border-[#2a2826] text-white"
                        placeholder="••••••••"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="remember"
                        checked={loginForm.rememberMe}
                        onCheckedChange={checked => setLoginForm({...loginForm, rememberMe: checked})}
                        className="border-[#2a2826] data-[state=checked]:bg-amber-500"
                      />
                      <label htmlFor="remember" className="text-sm text-gray-400 cursor-pointer">
                        Remember me
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMode("forgot")}
                      className="text-sm text-amber-400 hover:text-amber-300"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                    data-testid="login-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
                  </Button>
                </form>

                <p className="text-center text-sm text-gray-400">
                  New to 1895?{" "}
                  <button
                    onClick={startSignup}
                    className="text-amber-400 hover:text-amber-300 font-medium"
                  >
                    Create an account
                  </button>
                </p>
              </motion.div>
            )}

            {/* MULTI-STEP SIGNUP */}
            {mode === "signup" && (
              <motion.div
                key="signup"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                {/* Mobile Logo */}
                <div className="text-center lg:hidden">
                  <Logo1895 variant="full" className="mx-auto" width={140} />
                </div>

                {/* Progress Bar */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-amber-400 font-medium">Step {signupStep} of 4</span>
                    <span className="text-gray-500">{SIGNUP_STEPS[signupStep - 1].title}</span>
                  </div>
                  <div className="h-1.5 bg-[#1a1918] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${(signupStep / 4) * 100}%` }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    />
                  </div>
                  {/* Step Indicators */}
                  <div className="flex justify-between px-1">
                    {SIGNUP_STEPS.map((step) => (
                      <div key={step.id} className="flex flex-col items-center">
                        <div 
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                            step.id < signupStep 
                              ? 'bg-amber-500 text-black' 
                              : step.id === signupStep 
                                ? 'bg-amber-500/20 text-amber-400 border-2 border-amber-500' 
                                : 'bg-[#1a1918] text-gray-600'
                          }`}
                        >
                          {step.id < signupStep ? <Check className="w-4 h-4" /> : step.id}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Step Title */}
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {SIGNUP_STEPS[signupStep - 1].title}
                  </h2>
                </div>

                {/* Step Content */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={signupStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {renderSignupStep()}
                  </motion.div>
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="flex gap-3 pt-4">
                  {signupStep > 1 ? (
                    <Button
                      type="button"
                      onClick={handlePrevStep}
                      variant="outline"
                      className="flex-1 h-12 border-[#2a2826] text-white hover:bg-[#1a1918]"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => setMode("login")}
                      variant="outline"
                      className="flex-1 h-12 border-[#2a2826] text-white hover:bg-[#1a1918]"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Sign In
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    disabled={loading}
                    className="flex-1 h-12 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                    data-testid="signup-next"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : signupStep === 4 ? (
                      <>
                        Create Account
                        <Sparkles className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* FORGOT PASSWORD */}
            {mode === "forgot" && (
              <motion.div
                key="forgot"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <button
                  onClick={() => setMode("login")}
                  className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </button>

                {!forgotSent ? (
                  <>
                    <div>
                      <h2 className="text-xl font-semibold text-white">Reset your password</h2>
                      <p className="text-sm text-gray-400 mt-2">
                        Enter your email and we'll send you a link to reset your password.
                      </p>
                    </div>

                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div>
                        <Label className="text-gray-400 text-xs">Email</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <Input
                            type="email"
                            value={forgotEmail}
                            onChange={e => setForgotEmail(e.target.value)}
                            className="pl-10 bg-[#121110] border-[#2a2826] text-white"
                            placeholder="you@example.com"
                            data-testid="forgot-email"
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                        data-testid="forgot-submit"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Reset Link"}
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-white mb-2">Check your email</h2>
                    <p className="text-gray-400 text-sm mb-6">
                      If an account exists for {forgotEmail}, you'll receive a password reset link.
                    </p>
                    <Button
                      onClick={() => { setMode("login"); setForgotSent(false); setForgotEmail(""); }}
                      variant="outline"
                      className="border-[#2a2826] text-white hover:bg-[#1a1918]"
                    >
                      Return to login
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
